import React, { useEffect, useState, useRef } from "react";
import { motion, useMotionValue, useTransform } from "framer-motion";

import "./Carousel.css";

export interface CarouselItem {
  title: string;
  description: string;
  id: number;
  image?: string;
  alt?: string;
}

export interface CarouselProps {
  items?: CarouselItem[];
  baseWidth?: number;
  autoplay?: boolean;
  autoplayDelay?: number;
  pauseOnHover?: boolean;
  loop?: boolean;
  round?: boolean;
}

// Define our own interface for PanInfo to avoid import issues
interface DragInfo {
  offset: {
    x: number;
    y: number;
  };
  velocity: {
    x: number;
    y: number;
  };
}

const DEFAULT_ITEMS: CarouselItem[] = [
  {
    title: "Text Animations",
    description: "Cool text animations for your projects.",
    id: 1,
    image: "/placeholder.jpg",
  },
  {
    title: "Animations",
    description: "Smooth animations for your projects.",
    id: 2,
    image: "/placeholder.jpg",
  },
  {
    title: "Components",
    description: "Reusable components for your projects.",
    id: 3,
    image: "/placeholder.jpg",
  },
  {
    title: "Backgrounds",
    description: "Beautiful backgrounds and patterns for your projects.",
    id: 4,
    image: "/placeholder.jpg",
  },
  {
    title: "Common UI",
    description: "Common UI components are coming soon!",
    id: 5,
    image: "/placeholder.jpg",
  },
];

const DRAG_BUFFER = 0;
const VELOCITY_THRESHOLD = 500;
const GAP = 16;
const SPRING_OPTIONS = { type: "spring", stiffness: 300, damping: 30 };

function useRotationTransforms(
  x: any, 
  count: number, 
  trackItemOffset: number
) {
  // Create individual transform hooks at the top level
  const transforms = [];
  
  // We need to call these hooks unconditionally at the top level
  // So we'll create a fixed maximum number of hooks and only use what we need
  const t0 = useTransform(x, [-1 * trackItemOffset, 0, 1 * trackItemOffset], [90, 0, -90], { clamp: false });
  const t1 = useTransform(x, [-2 * trackItemOffset, -1 * trackItemOffset, 0], [90, 0, -90], { clamp: false });
  const t2 = useTransform(x, [-3 * trackItemOffset, -2 * trackItemOffset, -1 * trackItemOffset], [90, 0, -90], { clamp: false });
  const t3 = useTransform(x, [-4 * trackItemOffset, -3 * trackItemOffset, -2 * trackItemOffset], [90, 0, -90], { clamp: false });
  const t4 = useTransform(x, [-5 * trackItemOffset, -4 * trackItemOffset, -3 * trackItemOffset], [90, 0, -90], { clamp: false });
  const t5 = useTransform(x, [-6 * trackItemOffset, -5 * trackItemOffset, -4 * trackItemOffset], [90, 0, -90], { clamp: false });
  const t6 = useTransform(x, [-7 * trackItemOffset, -6 * trackItemOffset, -5 * trackItemOffset], [90, 0, -90], { clamp: false });
  const t7 = useTransform(x, [-8 * trackItemOffset, -7 * trackItemOffset, -6 * trackItemOffset], [90, 0, -90], { clamp: false });
  const t8 = useTransform(x, [-9 * trackItemOffset, -8 * trackItemOffset, -7 * trackItemOffset], [90, 0, -90], { clamp: false });
  const t9 = useTransform(x, [-10 * trackItemOffset, -9 * trackItemOffset, -8 * trackItemOffset], [90, 0, -90], { clamp: false });
  
  // Create an array with all our transforms
  const allTransforms = [t0, t1, t2, t3, t4, t5, t6, t7, t8, t9];
  
  // Only return the number we need
  return allTransforms.slice(0, Math.min(count, allTransforms.length));
}

export default function Carousel({
  items = DEFAULT_ITEMS,
  baseWidth = 300,
  autoplay = false,
  autoplayDelay = 3000,
  pauseOnHover = false,
  loop = false,
  round = false,
}: CarouselProps): React.ReactElement {
  const containerPadding = 16;
  const itemWidth = baseWidth - containerPadding * 2;
  const trackItemOffset = itemWidth + GAP;

  const carouselItems = loop ? [...items, items[0]] : items;
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const x = useMotionValue(0);
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const [isResetting, setIsResetting] = useState<boolean>(false);
  
  // Get all rotation transforms - this is called at the top level
  const rotateYTransforms = useRotationTransforms(x, carouselItems.length, trackItemOffset);

  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (pauseOnHover && containerRef.current) {
      const container = containerRef.current;
      const handleMouseEnter = () => setIsHovered(true);
      const handleMouseLeave = () => setIsHovered(false);
      container.addEventListener("mouseenter", handleMouseEnter);
      container.addEventListener("mouseleave", handleMouseLeave);
      return () => {
        container.removeEventListener("mouseenter", handleMouseEnter);
        container.removeEventListener("mouseleave", handleMouseLeave);
      };
    }
  }, [pauseOnHover]);

  useEffect(() => {
    if (autoplay && (!pauseOnHover || !isHovered)) {
      const timer = setInterval(() => {
        setCurrentIndex((prev) => {
          if (prev === items.length - 1 && loop) {
            return prev + 1; // Animate to clone.
          }
          if (prev === carouselItems.length - 1) {
            return loop ? 0 : prev;
          }
          return prev + 1;
        });
      }, autoplayDelay);
      return () => clearInterval(timer);
    }
  }, [
    autoplay,
    autoplayDelay,
    isHovered,
    loop,
    items.length,
    carouselItems.length,
    pauseOnHover,
  ]);

  const effectiveTransition = isResetting ? { duration: 0 } : SPRING_OPTIONS;

  const handleAnimationComplete = () => {
    if (loop && currentIndex === carouselItems.length - 1) {
      setIsResetting(true);
      x.set(0);
      setCurrentIndex(0);
      setTimeout(() => setIsResetting(false), 50);
    }
  };

  const handleDragEnd = (
    _: MouseEvent | TouchEvent | PointerEvent,
    info: DragInfo
  ): void => {
    const offset = info.offset.x;
    const velocity = info.velocity.x;
    if (offset < -DRAG_BUFFER || velocity < -VELOCITY_THRESHOLD) {
      if (loop && currentIndex === items.length - 1) {
        setCurrentIndex(currentIndex + 1); // Go to clone.
      } else {
        setCurrentIndex((prev) => Math.min(prev + 1, carouselItems.length - 1));
      }
    } else if (offset > DRAG_BUFFER || velocity > VELOCITY_THRESHOLD) {
      if (loop && currentIndex === 0) {
        setCurrentIndex(items.length - 1);
      } else {
        setCurrentIndex((prev) => Math.max(prev - 1, 0));
      }
    }
  };

  const dragProps = loop
    ? {}
    : {
        dragConstraints: {
          left: -trackItemOffset * (carouselItems.length - 1),
          right: 0,
        },
      };

  return (
    <div
      ref={containerRef}
      className={`carousel-container ${round ? "round" : ""}`}
      style={{
        width: `${baseWidth}px`,
        ...(round && { height: `${baseWidth}px`, borderRadius: "50%" }),
      }}
    >
      <motion.div
        className="carousel-track"
        drag="x"
        {...dragProps}
        style={{
          width: itemWidth,
          gap: `${GAP}px`,
          perspective: 1000,
          perspectiveOrigin: `${currentIndex * trackItemOffset + itemWidth / 2}px 50%`,
          x,
        }}
        onDragEnd={handleDragEnd}
        animate={{ x: -(currentIndex * trackItemOffset) }}
        transition={effectiveTransition}
        onAnimationComplete={handleAnimationComplete}
      >
        {carouselItems.map((item, index) => {
          // If we have more items than transforms, use the last transform as fallback
          const rotateY = index < rotateYTransforms.length 
            ? rotateYTransforms[index] 
            : rotateYTransforms[rotateYTransforms.length - 1];
            
          return (
            <motion.div
              key={index}
              className={`carousel-item ${round ? "round" : ""}`}
              style={{
                width: itemWidth,
                height: round ? itemWidth : "100%",
                rotateY,
                ...(round && { borderRadius: "50%" }),
              }}
              transition={effectiveTransition}
            >
              <div className={`carousel-item-header ${round ? "round" : ""}`}>
                {item.image && <img src={item.image} alt={item.alt || item.title} className="carousel-image" />}
              </div>
              <div className="carousel-item-content">
                <div className="carousel-item-title">{item.title}</div>
                <p className="carousel-item-description">{item.description}</p>
              </div>
            </motion.div>
          );
        })}
      </motion.div>
      <div className={`carousel-indicators-container ${round ? "round" : ""}`}>
        <div className="carousel-indicators">
          {items.map((_, index) => (
            <motion.div
              key={index}
              className={`carousel-indicator ${
                currentIndex % items.length === index ? "active" : "inactive"
              }`}
              animate={{
                scale: currentIndex % items.length === index ? 1.2 : 1,
              }}
              onClick={() => setCurrentIndex(index)}
              transition={{ duration: 0.15 }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
